import {
  api,
  CentresForUserResponse,
  CentresResponse,
  CentreTypeSelector,
  GetAllCentresParams,
  Centre,
  ExaminerCentre,
  ExaminerCentreAssignment,
  ExaminerCentreOption,
  ExaminerIdpCentre,
  ExaminerIdpCentreAssignment,
  ExaminerMainCentreAssignment,
  GetAvailableCentreOptionsParams,
  IdpCentre,
  IsHubCentreParams,
  MainCentre,
  NewExaminerIdpCentreAssignment,
  PagedResultOfExaminerCentreHistoryRecord,
  CertificationTypeEnum,
} from 'api';
import { queryCacheDuration } from 'const';
import { rtkQueryApi } from 'store/rtkQueryApi';
import { TableState } from 'types';
import { getPaginationParameters, getQueryStringParameters } from 'utils';

type ExaminerCentreListParams = TableState<
  never,
  { showHistorical: boolean; bcOnly?: boolean; idpOnly?: boolean }
> & {
  examinerId: number;
  certificationType: CertificationTypeEnum;
};

export const centresApi = rtkQueryApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllCentres: builder.query<CentresResponse, void>({
      query: () => {
        const input: GetAllCentresParams = { type: CentreTypeSelector.ALL };
        return api.centre.getAllCentres(input);
      },
      keepUnusedDataFor: queryCacheDuration,
    }),
    getCentres: builder.query<CentresResponse, { type: CentreTypeSelector }>({
      query: ({ type }) => api.centre.getAllCentres({ type }),
      keepUnusedDataFor: queryCacheDuration,
    }),
    getCentresForUser: builder.query<CentresForUserResponse, number>({
      query: (userId) => api.centre.getCentresForUser({ userId }),
    }),
    getExaminerCentreList: builder.query<
      PagedResultOfExaminerCentreHistoryRecord,
      ExaminerCentreListParams
    >({
      query: (params) => {
        const input = getQueryStringParameters({
          ...getPaginationParameters(params),
          filter: {
            ...params.filters,
            examinerId: params.examinerId,
            certificationType: params.certificationType,
          },
        });
        return api.examinerCentre.getExaminerCentreList(input);
      },
      providesTags: ['CentreAffiliation'],
    }),
    addBcMainCentreAffiliation: builder.mutation<ExaminerCentre, ExaminerCentreAssignment>({
      query: (data) => api.examinerCentre.addBcMainCentreAffiliation(data),
      invalidatesTags: ['CentreAffiliation', 'Examiner'],
    }),
    addSharedCentreAffiliation: builder.mutation<ExaminerCentre, ExaminerCentreAssignment>({
      query: (data) => api.examinerCentre.addSharedCentreAffiliation(data),
      invalidatesTags: ['CentreAffiliation', 'DistrictAffiliation', 'Examiner'],
    }),
    addIdpCentreAffiliation: builder.mutation<ExaminerIdpCentre, NewExaminerIdpCentreAssignment>({
      query: (data) => api.examinerCentre.addIdpCentreAffiliation(data),
      invalidatesTags: ['CentreAffiliation', 'DistrictAffiliation', 'Examiner'],
    }),
    endMainCentreAffiliation: builder.mutation<void, ExaminerMainCentreAssignment>({
      query: (data) => api.examinerCentre.endMainCentreAffiliation(data),
      invalidatesTags: ['CentreAffiliation', 'DistrictAffiliation', 'Examiner'],
    }),
    endSharedCentreAffiliation: builder.mutation<void, ExaminerCentreAssignment>({
      query: (data) => api.examinerCentre.endSharedCentreAffiliation(data),
      invalidatesTags: ['CentreAffiliation', 'DistrictAffiliation', 'Examiner'],
    }),
    endIdpCentreAffiliation: builder.mutation<void, ExaminerIdpCentreAssignment>({
      query: (data) => api.examinerCentre.endIdpCentreAffiliation(data),
      invalidatesTags: ['CentreAffiliation', 'DistrictAffiliation', 'Examiner'],
    }),
    getExaminerMainCentre: builder.query<
      MainCentre | null,
      { examinerId: number; certificationType: CertificationTypeEnum }
    >({
      query: (arg) => api.examinerCentre.getExaminerMainCentre(arg),
      providesTags: ['CentreAffiliation'],
    }),
    getExaminerSharedCentres: builder.query<
      ExaminerCentreOption[],
      { examinerId: number; certificationType: CertificationTypeEnum }
    >({
      query: (arg) => api.examinerCentre.getExaminerSharedCentres(arg),
      providesTags: ['CentreAffiliation'],
    }),
    getExaminerIdpSharedCentres: builder.query<
      ExaminerCentreOption[],
      { examinerId: number; certificationType: CertificationTypeEnum }
    >({
      query: (arg) => api.examinerCentre.getExaminerIdpSharedCentres(arg),
      providesTags: ['CentreAffiliation'],
    }),
    replaceMainCentre: builder.mutation<MainCentre, ExaminerMainCentreAssignment>({
      query: (data) => api.examinerCentre.replaceMainCentre(data),
      invalidatesTags: ['CentreAffiliation', 'DistrictAffiliation', 'Examiner'],
    }),
    getIdpCentreSuggestions: builder.query<IdpCentre[], string>({
      query: (arg) => api.examinerCentre.getIdpCentreSuggestions({ searchPhrase: arg }),
    }),
    getIdpCentreNumberSuggestions: builder.query<string[], string>({
      query: (arg) => api.examinerCentre.getIdpCentreNumberSuggestions({ searchPhrase: arg }),
    }),
    checkIfAssignedToAnyCentre: builder.query<
      boolean,
      { examinerId: number; certificationType: CertificationTypeEnum }
    >({
      query: (arg) => api.examinerCentre.checkIfAssignedToAnyCentre(arg),
      providesTags: ['CentreAffiliation'],
    }),
    getAvailableCentreOptions: builder.query<Centre[], GetAvailableCentreOptionsParams>({
      query: (params) => api.examinerCentre.getAvailableCentreOptions(params),
      providesTags: ['CentreAffiliation'],
    }),
    isHubCentre: builder.query<boolean, IsHubCentreParams>({
      query: (params) => api.centre.isHubCentre(params),
    }),
  }),
});

export const {
  useGetAllCentresQuery,
  useGetCentresQuery,
  useGetExaminerCentreListQuery,
  useAddBcMainCentreAffiliationMutation,
  useAddSharedCentreAffiliationMutation,
  useAddIdpCentreAffiliationMutation,
  useEndMainCentreAffiliationMutation,
  useEndSharedCentreAffiliationMutation,
  useEndIdpCentreAffiliationMutation,
  useGetExaminerMainCentreQuery,
  useGetExaminerSharedCentresQuery,
  useGetExaminerIdpSharedCentresQuery,
  useReplaceMainCentreMutation,
  useGetIdpCentreSuggestionsQuery,
  useGetIdpCentreNumberSuggestionsQuery,
  useCheckIfAssignedToAnyCentreQuery,
  useGetAvailableCentreOptionsQuery,
  useIsHubCentreQuery,
  useGetCentresForUserQuery,
} = centresApi;
